// legalObligationsManager.util.ts

import { db, firebaseImport, FirebaseTimestamp, auth } from '../../config/main_config'; // Adjust the path as needed
import { createToast } from './createToast.util'; // Utility for showing toast notifications
import { closeModal } from './closeModal.util'; // Utility for closing modals
import { waitForCompanyID } from './waitForCompanyID.util';
import { openModal } from './openModal.util';
import { nameFromUID } from './nameFromUID.util';
import Tableordion from './tableordion.util';
import { sanitise } from './sanitise.util';

/**
 * Interface representing a Legal Obligation.
 */
interface LegalObligation {
    id: string;
    addedBy: string;
    dateAdded: firebaseImport.firestore.Timestamp | Date | Object;
    dateUpdated: firebaseImport.firestore.Timestamp | Date | Object;
    owner: string;
    requirement: string;
    updateMethod: string;
    updatedBy: string;
}

/**
 * Fetches legal obligations data from Firestore.
 * 
 * @returns A promise that resolves to an array of LegalObligation objects.
 */
export async function fetchLegalObligations(): Promise<LegalObligation[]> {
    const companyID = await waitForCompanyID(); // Dynamic retrieval of companyID
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = db.doc(docPath);

    try {
        const doc = await docRef.get();
        if (doc.exists) {
            const data = doc.data()?.legalObligations;
            if (data && typeof data === 'object') {
                const legalObligations: LegalObligation[] = Object.entries(data).map(([id, obligation]: [string, any]) => ({
                    id,
                    addedBy: obligation.addedBy,
                    dateAdded: obligation?.dateAdded !== undefined ? obligation.dateAdded : firebaseImport.firestore.Timestamp.fromDate(new Date()),
                    dateUpdated: obligation?.dateUpdated !== undefined ? obligation.dateUpdated : firebaseImport.firestore.Timestamp.fromDate(new Date()),
                    owner: obligation.owner,
                    requirement: obligation.requirement,
                    updateMethod: obligation.updateMethod,
                    updatedBy: obligation.updatedBy,
                }));
                console.log('Fetched legal obligations:', legalObligations);
                return legalObligations;
            } else {
                console.warn('No legal obligations data found.');
                return [];
            }
        } else {
            console.warn('Legal obligations document does not exist.');
            return [];
        }
    } catch (error) {
        console.error('Error fetching legal obligations:', error);
        createToast({
            message: 'Failed to load legal obligations.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return [];
    }
}

/**
 * Renders the legal obligations into a tableordion.
 * 
 * @param legalObligations - Array of LegalObligation objects.
 */
export async function renderLegalObligationsTable(legalObligations: LegalObligation[]): Promise<void> {
    const tableContainer = document.querySelector('.tableordion-body');

    if (!tableContainer) {
        console.error('Legal Obligations table container not found.');
        return;
    }

    let html = ``;

    // Sort the legal obligations by requirement
    legalObligations.sort((a, b) => a.requirement.localeCompare(b.requirement, undefined, { numeric: true, sensitivity: 'base' }));

    for (const id in legalObligations) {
        let obligation = legalObligations[id];
        html += `
            <tr data-id="${obligation.id}" class="tableordion-row" style="pointer: default;">
                <td>${sanitise(obligation.requirement)}</td>
                <td>${await nameFromUID(obligation.owner)}</td>
                <td>${obligation.updateMethod}</td>
                <td>
                    <button class="action-btn  edit-legalObligation-btn" data-id="${obligation.id}">
                        <i class="fa-duotone fa-solid fa-pencil edit-icon" alt="Edit"></i>
                    </button>
                    <button class="action-btn" onclick="deleteLegalObligation('${obligation.id}')" data-id="${obligation.id}">
                        <i class="fa-duotone fa-trash delete-icon color-red" alt="Delete"></i>
                    </button>
                </td>
            </tr>
        `;
    }

    html += ``;

    tableContainer.innerHTML = html;

    // Attach event listeners to edit buttons
    attachEditButtonsListeners();
    const accordions = new Tableordion('.tableordion-row')
}

/**
 * Attaches event listeners to all edit buttons in the tableordion.
 */
function attachEditButtonsListeners(): void {
    const editButtons = document.querySelectorAll('.edit-legalObligation-btn');

    editButtons.forEach(button => {
        button.addEventListener('click', async (event) => {
            const target = event.currentTarget as HTMLElement;
            const obligationId = target.getAttribute('data-id');

            if (!obligationId) {
                console.error('Legal Obligation ID not found on the edit button.');
                return;
            }

            const legalObligations = (window as any).legalObligationsRegister as LegalObligation[];
            const obligation = legalObligations.find(lo => lo.id === obligationId);

            if (!obligation) {
                console.error('Legal Obligation not found for the given ID.');
                return;
            }

            openEditLegalObligationModal(obligation);
        });
    });
}

/**
 * Opens the edit legal obligation modal and populates it with current data.
 * 
 * @param obligation - The LegalObligation object to edit.
 */
export function openEditLegalObligationModal(obligation: LegalObligation): void {
    const modal = document.querySelector('[data-managedid="EditLegalObligationModal"]') as HTMLElement;
    if (!modal) {
        console.error('Edit Legal Obligation Modal not found.');
        return;
    }

    // Populate the modal fields
    const requirementElement = document.getElementById('requirement_edit') as HTMLInputElement;
    const updateMethodElement = document.getElementById('updateMethod_edit') as HTMLInputElement;
    const dateAddedElement = document.getElementById('dateAdded_edit') as HTMLInputElement;
    const dateUpdatedElement = document.getElementById('dateUpdated_edit') as HTMLInputElement;

    if (requirementElement && updateMethodElement && dateAddedElement && dateUpdatedElement) {

        console.log('obligation', obligation, typeof obligation.dateAdded, obligation.dateAdded)
        requirementElement.value = obligation.requirement;
        updateMethodElement.value = obligation.updateMethod;
        dateAddedElement.valueAsDate = obligation?.dateAdded instanceof FirebaseTimestamp
            ? obligation?.dateAdded instanceof firebaseImport.firestore.Timestamp
                ? obligation?.dateAdded.toDate()
                : obligation?.dateAdded instanceof Date
                    ? obligation?.dateAdded
                    : new Date()
            : obligation?.dateAdded instanceof Date
                ? obligation?.dateAdded
                : new Date();
        dateUpdatedElement.valueAsDate = obligation?.dateUpdated instanceof FirebaseTimestamp
            ? obligation?.dateUpdated instanceof firebaseImport.firestore.Timestamp
                ? obligation?.dateUpdated.toDate()
                : obligation?.dateUpdated instanceof Date
                    ? obligation?.dateUpdated
                    : new Date()
            : obligation?.dateUpdated instanceof Date
                ? obligation?.dateUpdated
                : new Date();
    } else {
        console.error('One or more modal input elements not found.');
        return;
    }

    // Store the obligation ID in the modal for reference during save
    modal.setAttribute('data-id', obligation.id);

    // Open the modal using your existing modal management function
    openModal('[data-managedid="EditLegalObligationModal"]');
}

/**
 * Adds a new legal obligation to Firestore.
 * 
 * @param requirement - The requirement text.
 * @param updateMethod - The method of update.
 */
export async function addLegalObligation(requirement: string, updateMethod: string, dateAdded: Date): Promise<void> {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = db.doc(docPath);

    try {
        const doc = await docRef.get();
        let legalObligations = doc.data()?.legalObligations;

        if (!legalObligations || typeof legalObligations !== 'object') {
            legalObligations = {};
        }

        // Generate a new ID
        const newId = generateNewId(legalObligations);

        // Create the new legal obligation object
        const newLegalObligation: LegalObligation = {
            id: newId,
            addedBy: auth.currentUser.uid, // Replace with actual user ID
            dateAdded: firebaseImport.firestore.Timestamp.fromDate(dateAdded),
            dateUpdated: firebaseImport.firestore.Timestamp.now(),
            owner: auth.currentUser.uid, // Replace with actual user ID
            requirement,
            updateMethod,
            updatedBy: auth.currentUser.uid, // Replace with actual user ID
        };

        // Add the new legal obligation to the existing map
        legalObligations[newId] = {
            addedBy: newLegalObligation.addedBy,
            dateAdded: newLegalObligation.dateAdded,
            dateUpdated: newLegalObligation.dateUpdated,
            owner: newLegalObligation.owner,
            requirement: newLegalObligation.requirement,
            updateMethod: newLegalObligation.updateMethod,
            updatedBy: newLegalObligation.updatedBy,
        };

        // Update Firestore
        await docRef.update({
            legalObligations
        });

        createToast({
            message: 'Legal Obligation added successfully.',
            customStyles: {
                backgroundColor: '#28a745',
                color: '#ffffff'
            }
        });

    } catch (error) {
        console.error('Error adding legal obligation:', error);
        createToast({
            message: 'Failed to add legal obligation.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
    }
}

/**
 * Edits an existing legal obligation in Firestore.
 * 
 * @param id - The ID of the legal obligation to edit.
 * @param requirement - The updated requirement text.
 * @param updateMethod - The updated method of update.
 */
export async function editLegalObligation(id: string, requirement: string, updateMethod: string, dateAdded: Date, lastUpdated: Date): Promise<void> {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = db.doc(docPath);

    try {
        const doc = await docRef.get();
        let legalObligations = doc.data()?.legalObligations;

        if (!legalObligations || typeof legalObligations !== 'object' || !legalObligations[id]) {
            console.error('Legal Obligation not found.');
            createToast({
                message: 'Legal Obligation not found.',
                customStyles: {
                    backgroundColor: '#ff0000',
                    color: '#ffffff'
                }
            });
            return;
        }

        // Update the legal obligation
        legalObligations[id] = {
            ...legalObligations[id],
            requirement,
            updateMethod,
            dateAdded: firebaseImport.firestore.Timestamp.fromDate(dateAdded),
            dateUpdated: firebaseImport.firestore.Timestamp.fromDate(lastUpdated),
            updatedBy: auth.currentUser.uid, // Replace with actual user ID
        };

        // Update Firestore
        await docRef.update({
            legalObligations
        });

        createToast({
            message: 'Legal Obligation updated successfully.',
            customStyles: {
                backgroundColor: '#28a745',
                color: '#ffffff'
            }
        });

    } catch (error) {
        console.error('Error editing legal obligation:', error);
        createToast({
            message: 'Failed to edit legal obligation.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
    }
}

/**
 * Sets up a real-time listener for legal obligations using onSnapshot.
 */
export function setupLegalObligationsListener(): void {
    waitForCompanyID().then(companyID => {
        const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
        const docRef = db.doc(docPath);

        docRef.onSnapshot(docSnapshot => {
            if (docSnapshot.exists) {
                const data = docSnapshot.data()?.legalObligations;
                if (data && typeof data === 'object') {
                    const legalObligations: LegalObligation[] = Object.entries(data).map(([id, obligation]: [string, any]) => ({
                        id,
                        addedBy: obligation.addedBy,
                        dateAdded: obligation.dateAdded instanceof FirebaseTimestamp
                            ? obligation.dateAdded.toDate()
                            : obligation.dateAdded instanceof Date
                                ? obligation.dateAdded
                                : new Date(),
                        dateUpdated: obligation.dateUpdated instanceof FirebaseTimestamp
                            ? obligation.dateUpdated.toDate()
                            : obligation.dateUpdated instanceof Date
                                ? obligation.dateUpdated
                                : new Date(),
                        owner: obligation.owner,
                        requirement: obligation.requirement,
                        updateMethod: obligation.updateMethod,
                        updatedBy: obligation.updatedBy,
                    }));
                    (window as any).legalObligationsRegister = legalObligations;
                    renderLegalObligationsTable(legalObligations);
                }
            }
        }, error => {
            console.error('Error with onSnapshot listener:', error);
            createToast({
                message: 'Real-time updates failed.',
                customStyles: {
                    backgroundColor: '#ff0000',
                    color: '#ffffff'
                }
            });
        });
    });
}

/**
 * Generates a new unique ID for a legal obligation.
 * 
 * @param legalObligations - The current legal obligations map.
 * @returns A new unique ID as a string.
 */
function generateNewId(legalObligations: { [key: string]: any }): string {
    const existingIds = Object.keys(legalObligations).map(id => parseInt(id, 10)).filter(id => !isNaN(id));
    const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 0;
    return (maxId + 1).toString();
}

/**
 * Deletes a legal obligation from Firestore.
 * 
 * @param id - The ID of the legal obligation to delete.
 */
export async function deleteLegalObligation(id: string): Promise<void> {
    const companyID = await waitForCompanyID();
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = db.doc(docPath);


    if (confirm(`Are you sure you want to delete this legal obligation?`) === true) {
        try {
            await docRef.update({
                [`legalObligations.${id}`]: firebaseImport.firestore.FieldValue.delete()
            });

            createToast({
                message: 'Legal Obligation deleted successfully.',
                customStyles: {
                    backgroundColor: '#28a745',
                    color: '#ffffff'
                }
            });

        } catch (error) {
            console.error('Error deleting legal obligation:', error);
            createToast({
                message: 'Failed to delete legal obligation.',
                customStyles: {
                    backgroundColor: '#ff0000',
                    color: '#ffffff'
                }
            });
        }
    } else {
        console.log('Legal obligation deletion cancelled.');
    }
}

/**
 * Handles the addition of a new legal obligation.
 * 
 * @param event - The event triggered by the save button.
 */
export async function handleAddLegalObligation(event: Event): Promise<void> {
    event.preventDefault();

    // Retrieve input values
    const requirement = (document.getElementById('requirement_add') as HTMLInputElement).value.trim();
    const updateMethod = (document.getElementById('updateMethod_add') as HTMLInputElement).value.trim();
    const dateAdded = (document.getElementById('dateAdded_add') as HTMLInputElement).valueAsDate;

    // Validate inputs
    if (!requirement || !updateMethod || !dateAdded) {
        createToast({
            message: 'All fields are required.',
            customStyles: {
                backgroundColor: '#ffcc00',
                color: '#000000'
            }
        });
        return;
    }

    // Add the new legal obligation
    await addLegalObligation(requirement, updateMethod, dateAdded);

    // Close the modal
    closeModal(document.querySelector('[data-managedid="AddLegalObligationModal"]') as HTMLElement);

    // Clear input fields
    (document.getElementById('requirement_add') as HTMLInputElement).value = '';
    (document.getElementById('updateMethod_add') as HTMLInputElement).value = '';
}

/**
 * Handles the editing of an existing legal obligation.
 * 
 * @param event - The event triggered by the save button.
 */
export async function handleEditLegalObligation(event: Event): Promise<void> {
    event.preventDefault();

    const modal = document.querySelector('[data-managedid="EditLegalObligationModal"]') as HTMLElement;
    const obligationId = modal.getAttribute('data-id');

    if (!obligationId) {
        console.error('Legal Obligation ID not found in the modal.');
        createToast({
            message: 'An error occurred. Please try again.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return;
    }

    // Retrieve updated input values
    const requirement = (document.getElementById('requirement_edit') as HTMLInputElement).value.trim();
    const updateMethod = (document.getElementById('updateMethod_edit') as HTMLInputElement).value.trim();
    const dateAdded = (document.getElementById('dateAdded_edit') as HTMLInputElement).valueAsDate;
    const lastUpdated = (document.getElementById('dateUpdated_edit') as HTMLInputElement).valueAsDate;

    // Validate inputs
    if (!requirement || !updateMethod || !dateAdded || !lastUpdated) {
        createToast({
            message: 'All fields are required.',
            customStyles: {
                backgroundColor: '#ffcc00',
                color: '#000000'
            }
        });
        return;
    }

    // Edit the legal obligation
    await editLegalObligation(obligationId, requirement, updateMethod, dateAdded, lastUpdated);

    // Close the modal
    closeModal(document.querySelector('[data-managedid="EditLegalObligationModal"]') as HTMLElement);
}